import { useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "next-i18next";
import { EnumRankingType } from "src/components/common/ranking/types";
import { Typography, TypographyProps } from "src/components-mui/base/typography/typography";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { getPerformanceColor } from "src/utils/color-util";
import { isNullOrUndefined } from "src/utils/common-util";
import { toSxArray } from "src/utils/mui/sx/sx";
import { IRanking } from "./ranking";

const rankingPlaceSx = {
    whiteSpace: "nowrap",
    overflowX: "hidden",
    textOverflow: "ellipsis",
    textAlign: "right",
    display: "block",
};

const rankingValueBoxSx = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-all",
};

export type IRankingValueSize = TypographyProps["fontSize"];

interface IRankingValue {
    fontWeight?: TypographyProps["fontWeight"];
    hidePoints?: boolean;
    value?: string;
}

function hasPlaceProperty(data: unknown): data is { place: number } {
    return typeof data === "object" && data !== null && "place" in data && typeof (data as { place?: unknown }).place === "number";
}

function RankingPlaceValue({ fontWeight, hidePoints, size, data, fractionDigits }: IRanking & IRankingValue) {
    const { t } = useTranslation("common");
    const { formatNumber, formatOrdinals } = useNumberFormatter();
    const valueDisplay = useMemo(
        () => (isNullOrUndefined(data.value) ? "-" : formatNumber(data.value, fractionDigits ?? 0)),
        [data.value, fractionDigits, formatNumber]
    );
    const placeValue = useMemo(() => (hasPlaceProperty(data) ? `${formatOrdinals(data.place)} ${t("place")}` : "-"), [data, formatOrdinals, t]);
    return (
        <Box sx={rankingPlaceSx} data-test-id="ranking-value">
            <Typography
                sx={{
                    fontSize: size,
                    fontWeight: fontWeight ?? "bold",
                }}
            >
                {placeValue}
            </Typography>
            {!hidePoints && (
                <Typography
                    sx={{
                        fontSize: size,
                        fontWeight: "normal",
                    }}
                >{`${valueDisplay} ${t("points")}`}</Typography>
            )}
        </Box>
    );
}

function RankingValueBox({ fontWeight, size, data, fractionDigits, value }: IRanking & IRankingValue) {
    const { t } = useTranslation("common");
    const { formatMoney, formatNumber, formatPerformance, formatVolatility, formatBeta } = useNumberFormatter();
    const memoized = useMemo(() => {
        if (value) {
            return { valueDisplay: value, color: undefined };
        }
        let valueDisplay = "-";
        let color: string | undefined;
        if (data.value !== null) {
            switch (data.type) {
                case EnumRankingType.InvestedCapital:
                    valueDisplay = formatMoney(data.value, t);
                    break;
                case EnumRankingType.MaxLoss:
                    valueDisplay = formatPerformance(data.value, fractionDigits ?? 1);
                    break;
                case EnumRankingType.Volatility:
                    valueDisplay = formatVolatility(data.value, fractionDigits ?? 1);
                    break;
                case EnumRankingType.Performance:
                    valueDisplay = formatPerformance(data.value, fractionDigits ?? 1);
                    color = getPerformanceColor(valueDisplay);
                    break;
                case EnumRankingType.SharpRatio:
                    valueDisplay = formatNumber(data.value, fractionDigits ?? 1);
                    break;
                case EnumRankingType.Reservations:
                    valueDisplay = formatNumber(data.value);
                    break;
                case EnumRankingType.Beta:
                    valueDisplay = formatBeta(data.value, fractionDigits ?? 1);
                    break;
            }
        }
        return { valueDisplay, color };
    }, [value, data.value, data.type, fractionDigits, formatMoney, formatNumber, formatPerformance, formatVolatility, formatBeta, t]);
    const sx = memoized.color ? { ...rankingValueBoxSx, color: memoized.color } : rankingValueBoxSx;
    return (
        <Box
            data-test-id="ranking-value"
            sx={[
                {
                    fontSize: size,
                    fontWeight: fontWeight ?? "inherit",
                },
                ...toSxArray(sx),
            ]}
        >
            {memoized.valueDisplay}
        </Box>
    );
}

export const RankingValue = (props: IRanking & IRankingValue) => {
    if (props.data.type === EnumRankingType.RankingPlace) {
        return <RankingPlaceValue {...props} />;
    }
    return <RankingValueBox {...props} />;
};
